<template>
  <div>
    <transition name="modal">
      <delete-modal
        v-if="showDeleteModal"
        :loading="loadingDelete"
        @close="closeDeleteModal()"
        @confirmDelete="deleteDiet"
      ></delete-modal>
    </transition>

    <transition name="modal">
      <diet-modal
        v-if="showDietDetail"
        :diet="diet"
        @close="showDietDetail = false"
        @printMeal="printMeal"
        @printAllMeals="printAllMeals"
      ></diet-modal>
    </transition>

    <transition name="modal">
      <diet-modal
        v-if="previousStateToView"
        :diet="previousStateToView"
        @close="previousStateToView = null"
      ></diet-modal>
    </transition>

    <div v-if="!edit">
      <div class="card w-full">
        <div class="flex justify-between items-center">
          <p class="font-medium">ID: #{{ diet.id }} - {{ diet.name }}</p>
          <div class="flex items-center">
            <div class="flex space-x-2">
              <button
                class="secondary-button"
                @click.prevent="showDietDetail = true"
              >
                Detalle
              </button>

              <edit-button
                v-if="canEdit && hasPermission('edit_diets')"
                @click.prevent="edit = true"
              />

              <delete-button
                v-if="canDelete && hasPermission('delete_diets')"
                class="block delete-icon-button"
                @click="openDeleteModal()"
              />
            </div>
          </div>
        </div>

        <p
          v-if="diet.status === 'excluded'"
          class="text-error-500 mt-2 text-sm"
        >
          Esta dieta está excluída del plan actualemente.
        </p>

        <router-link
          v-if="$route.name === 'petsDetail' && diet.quote_id"
          :to="{ name: 'quotesDetail', params: { id: diet.quote_id } }"
          class="system-button underline"
          >Plan #{{ diet.quote_id }}</router-link
        >
        <span v-else-if="$route.name === 'petsDetail'" class="text-gray-400">Sin plan asignado</span>

        <div>
          <div class="div-line"></div>

          <p class="mt-6 font-bold">Historial de cambios</p>

          <table class="table-auto text-sm mt-2 w-full">
            <thead class="text-left">
              <tr class="bg-primary-100">
                <th class="px-4 py-3 rounded-l-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Evento</th>
                <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Fecha</th>
                <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Estado anterior</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="history in diet.diet_histories" :key="history.id" class="table-row">
                <td class="table-cell">
                  {{ history.event }}
                </td>
                <td class="table-cell">{{ formatDatetime(history.created_at) }}</td>

                <td class="table-cell">
                  <div class="flex">
                    <button
                      class="secondary-button"
                      @click.prevent="selectHistory(history)"
                    >
                      Detalle
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="canChangeDiet">
        <button
          v-if="
            diet.pet.diets.length > 1 && !change && hasPermission('edit_diets')
          "
          class="secondary-button block mt-2"
          @click.prevent="change = true"
        >
          Cambiar Dieta
        </button>

        <div v-if="change">
          <button class="cancel-button mt-2" @click.prevent="change = false">
            Cancelar
          </button>

          <form @submit.prevent="changeDiet">
            <div class="flex flex-wrap space-x-4>">
              <div class="card-option-wrapper">
                <div
                  v-for="petDiet in diet.pet.diets"
                  :key="petDiet.id"
                  class="mt-2 card-option"
                  :class="[
                    {
                      'selected-border': selectedDietId === petDiet.id,
                    },
                  ]"
                  @click.prevent="selectOption(petDiet.id)"
                >
                  <p class="text-sm font-medium">#{{ petDiet.id }}</p>

                  <div class="mt-2 text-sm">
                    <p class="font-medium">Precio por semana</p>
                    <p>{{ integerToCurrency(petDiet.weekly_price) }}</p>
                  </div>

                  <div class="mt-2 text-sm">
                    <p class="font-medium">Cantidad total</p>
                    <p>{{ petDiet.total_quantity }}g</p>
                  </div>
                </div>
              </div>
            </div>

            <primary-button
              v-if="selectedDietId && selectedDietId !== diet.id"
              class="mx-0 mt-4"
              :loading="loadingChange"
              type="submit"
              >Guardar</primary-button
            >
          </form>
        </div>
      </div>
    </div>

    <div v-else>
      <button class="cancel-button mt-2" @click.prevent="edit = false">
        Cancelar
      </button>

      <diet-form
        :pet="pet"
        :dietCategories="dietCategories"
        :dietStages="dietStages"
        :dietTypes="dietTypes"
        :diet="prepareDietFormat()"
        @dietUpdated="$emit('dietUpdated')"
        @close="edit = false"
      ></diet-form>
    </div>
  </div>
</template>

<script>
import DietForm from "../forms/DietForm";
import DietModal from "../modals/DietModal";
import deleteMixin from "@/mixins/deleteMixin";
import { formatDatetime } from "@/utils/dateFormatter";
import { integerToCurrency } from "@/utils/currencyFormatter";
import api from "@/api";
export default {
  name: "Diet",

  components: {
    DietForm,
    DietModal,
  },

  props: {
    diet: Object,
    pet: Object,
    dietCategories: Array,
    dietStages: Array,
    dietTypes: Array,
    canEdit: false,
    canDelete: false,
    canChangeDiet: false,
  },

  data() {
    return {
      edit: false,
      change: false,
      selectedDietId: null,
      loadingChange: false,
      loadingDelete: false,
      previousStateToView: null,
      showDietDetail: false,
    };
  },

  mixins: [deleteMixin],

  created() {
    this.selectedDietId = this.diet.id;
  },

  methods: {
    formatDatetime,

    integerToCurrency,

    getOptions() {
      let options = [];

      if (this.canEdit && this.hasPermission("edit_diets")) {
        options.push({
          action: "edit",
          label: "Editar",
        });
      }

      if (this.canDelete && this.hasPermission("delete_diets")) {
        options.push({
          action: "delete",
          label: "Eliminar",
        });
      }

      return options;
    },

    selectHistory(history) {
      const previousState = JSON.parse(history.previous_state);
      this.previousStateToView = previousState;
    },

    prepareDietFormat() {
      let dietFormatted = JSON.parse(JSON.stringify(this.diet));

      dietFormatted.meals.forEach((meal) => {
        meal.portions.forEach((portion) => {
          portion.diet_category_id = portion.diet_subcategory.diet_category_id;
          portion.saved = true;

          delete portion.id;
          delete portion.meal_id;
          delete portion.meal_id;
          delete portion.diet_subcategory;
          delete portion.created_at;
          delete portion.updated_at;
        });

        delete meal.id;
        delete meal.diet_id;
        delete meal.created_at;
        delete meal.updated_at;
      });

      delete dietFormatted.collapsed;
      delete dietFormatted.created_at;
      delete dietFormatted.updated_at;
      delete dietFormatted.diet_stage;
      delete dietFormatted.diet_type;
      delete dietFormatted.quote_id;

      return dietFormatted;
    },

    selectOption(dietId) {
      if (this.selectedDietId === dietId) {
        this.selectedDietId = null;
      } else {
        this.selectedDietId = dietId;
      }
    },

    async changeDiet() {
      this.loadingChange = true;
      try {
        const response = await api.quotes.changeDiet({
          current_diet_id: this.diet.id,
          new_diet_id: this.selectedDietId,
        });
        this.$store.dispatch("successSnack", response.message);
        this.$emit("dietChanged");
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingChange = false;
    },

    async deleteDiet() {
      try {
        const response = await api.diets.delete(this.diet.id);
        this.$store.dispatch("successSnack", response.message);
        this.closeDeleteModal();
        this.$emit("dietUpdated");
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.closeDeleteModal();
    },

    async printMeal(mealId) {
      try {
        const response = await api.diets.printMeal(mealId);
        this.$store.dispatch("successSnack", response.message);
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
    },

    async printAllMeals() {
      try {
        const response = await api.diets.printAllMeals(this.diet.id);
        this.$store.dispatch("successSnack", response.message);
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
    },
  },
};
</script>